import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import {
  formatRevisionMoneyDisplayLabel,
  type RevisionReviewInvoice,
  type RevisionReviewMilestoneCard,
  type RevisionReviewTag,
  type RevisionReviewView,
} from "@handshake/core-types";

@Component({
  selector: "core-revision-review",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./core-revision-review.component.html",
  styleUrl: "./core-revision-review.component.scss",
})
export class CoreRevisionReviewComponent {
  @Input({ required: true }) view!: RevisionReviewView;
  @Input() variant: "client" | "business" = "business";
  @Input() currency = "SGD";
  @Input() firmName = "";
  /** Whole Handshake fee percent. The column shows only the share the client pays. */
  @Input() feePercent: number | null = null;
  /** 0 means the client pays the fee. 100 means the business pays it. */
  @Input() payableByBusiness: number | null = null;

  get isRecord(): boolean {
    return this.view.changedOnly === true;
  }

  get datesChanged(): boolean {
    return Boolean(this.view.projectDates.previousStartDate || this.view.projectDates.previousEndDate);
  }

  get showClientFee(): boolean {
    return this.clientFeeShare > 0 && (Number(this.feePercent) || 0) > 0;
  }

  get scheduledInvoices(): RevisionReviewInvoice[] {
    return this.view.invoices
      .map((invoice, index) => ({ invoice, index }))
      .sort((left, right) => {
        if (left.invoice.dueDate === right.invoice.dueDate) return left.index - right.index;
        if (!left.invoice.dueDate) return 1;
        if (!right.invoice.dueDate) return -1;
        return left.invoice.dueDate < right.invoice.dueDate ? -1 : 1;
      })
      .map((item) => item.invoice);
  }

  get additionAdjustments() {
    return this.view.adjustments.filter((adjustment) => adjustment.kind === "addition");
  }

  get deductionAdjustments() {
    return this.view.adjustments.filter((adjustment) => adjustment.kind === "reduction");
  }

  private get clientFeeShare(): number {
    if (this.payableByBusiness == null) return 0;
    return Math.max(0, 100 - Number(this.payableByBusiness));
  }

  isDetailedMilestone(milestone: RevisionReviewMilestoneCard): boolean {
    return Boolean(milestone.tag) || milestone.submilestones.some((sub) => sub.tag);
  }

  invoiceAmount(invoice: RevisionReviewInvoice): number {
    return invoice.lines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  }

  clientFee(invoice: RevisionReviewInvoice): number {
    const fee = (this.invoiceAmount(invoice) * (Number(this.feePercent) || 0)) / 100;
    return (fee * this.clientFeeShare) / 100;
  }

  money(value: number | null | undefined): string {
    return (Number(value) || 0).toFixed(2);
  }

  taggedMoney(value: number | null | undefined): string {
    return `${this.currency} ${this.money(value)}`;
  }

  signedMoney(value: number | null | undefined): string {
    const amount = Number(value) || 0;
    const formatted = this.money(Math.abs(amount));
    if (amount > 0) return `+${formatted}`;
    if (amount < 0) return `-${formatted}`;
    return formatted;
  }

  dateLabel(value: string | null | undefined): string {
    if (!value) return "—";
    const parts = value.split("-");
    if (parts.length !== 3) return value;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[Number(parts[1]) - 1];
    const day = String(Number(parts[2]));
    return month ? `${day} ${month} ${parts[0]}` : value;
  }

  tagLabel(tag: RevisionReviewTag | null | undefined): string {
    if (tag === "changed") return "Changed";
    if (tag === "new") return "New";
    if (tag === "deleted") return "Deleted";
    return "";
  }

  moneyStateLabel(label: string | null | undefined): string {
    return formatRevisionMoneyDisplayLabel(label);
  }

  pushedNote(days: number | null | undefined): string | null {
    if (days == null || days === 0) return null;
    if (days > 0) return `Pushed back by ${days} days`;
    return `Brought forward by ${Math.abs(days)} days`;
  }

  netChangeClass(net: number): string {
    if (net > 0) return "amount-increase";
    if (net < 0) return "amount-decrease";
    return "";
  }
}
